import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  MapPin, Phone, Mail, Star, Trophy, Users, TrendingUp,
  CheckCircle, Award, Clock, BookOpen, GraduationCap, Calendar
} from 'lucide-react';
import {
  upsertMetaByName,
  upsertMetaByProperty,
  upsertCanonical,
  upsertJsonLd,
  removeJsonLd
} from '../lib/seoHead';
import { LOCAL_PAGES } from '../config/localSeoPages';

const SITE_URL = 'https://vidya365.in';
const PHONE = '+91 85041 10365';
const ADDRESS = 'Unit 8, Apsara Arcade, Near Karol Bagh Metro Gate 7, Pusa Road, Karol Bagh, New Delhi - 110005';
const NEARBY = ['Rajendra Place', 'Patel Nagar', 'Dev Nagar', 'Ranjeet Nagar', 'Bapa Nagar', 'Pusa Road', 'Connaught Place', 'Paharganj', 'Rajinder Nagar', 'Inderlok'];

const LocalLandingPage = ({ slug: propSlug }) => {
  const params = useParams();
  const slug = propSlug || params.slug;
  const config = LOCAL_PAGES[slug];

  useEffect(() => {
    if (!config) return;
    const canonical = `${SITE_URL}/${slug}`;

    document.title = config.title;
    upsertMetaByName('description', config.description);
    upsertMetaByName('keywords', config.keywords.join(', '));
    upsertCanonical(canonical);

    // Open Graph
    upsertMetaByProperty('og:title', config.title);
    upsertMetaByProperty('og:description', config.description);
    upsertMetaByProperty('og:url', canonical);
    upsertMetaByProperty('og:type', 'website');

    // Twitter
    upsertMetaByName('twitter:title', config.title);
    upsertMetaByName('twitter:description', config.description);

    // Geo
    upsertMetaByName('geo.region', 'IN-DL');
    upsertMetaByName('geo.placename', 'Karol Bagh, New Delhi');
    upsertMetaByName('geo.position', '28.6519;77.1909');
    upsertMetaByName('ICBM', '28.6519, 77.1909');

    // LocalBusiness + EducationalOrganization JSON-LD
    upsertJsonLd('localbusiness', {
      '@context': 'https://schema.org',
      '@type': ['EducationalOrganization', 'LocalBusiness'],
      '@id': `${SITE_URL}#org`,
      name: 'Vidya365 Coaching Institute',
      alternateName: 'Vidya365',
      description: config.description,
      url: SITE_URL,
      logo: `${SITE_URL}/logo512.png.svg`,
      image: `${SITE_URL}/logo512.png.svg`,
      telephone: PHONE,
      email: 'info@vidya365.com',
      priceRange: '₹₹',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Unit 8, Apsara Arcade, Near Karol Bagh Metro Gate 7, Pusa Road',
        addressLocality: 'Karol Bagh',
        addressRegion: 'Delhi',
        postalCode: '110005',
        addressCountry: 'IN'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 28.6519,
        longitude: 77.1909
      },
      areaServed: [
        { '@type': 'City', name: 'Delhi' },
        { '@type': 'City', name: 'New Delhi' },
        ...NEARBY.map((n) => ({ '@type': 'Place', name: n }))
      ],
      openingHoursSpecification: [{
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'],
        opens: '09:00',
        closes: '20:00'
      }],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.6',
        reviewCount: '263',
        bestRating: '5',
        worstRating: '1'
      },
      sameAs: [
        'https://www.facebook.com/vidya365',
        'https://www.instagram.com/vidya365',
        'https://www.youtube.com/@vidya365'
      ]
    });

    // Course JSON-LD
    upsertJsonLd('course', {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: config.courseName,
      description: config.description,
      provider: {
        '@type': 'EducationalOrganization',
        '@id': `${SITE_URL}#org`,
        name: 'Vidya365',
        url: SITE_URL
      },
      offers: {
        '@type': 'Offer',
        category: 'Coaching',
        availability: 'https://schema.org/InStock',
        priceCurrency: 'INR',
        url: canonical
      },
      hasCourseInstance: [{
        '@type': 'CourseInstance',
        courseMode: ['Offline', 'Online'],
        location: { '@type': 'Place', name: 'Karol Bagh, New Delhi' }
      }]
    });

    // BreadcrumbList
    upsertJsonLd('breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: config.h1, item: canonical }
      ]
    });

    // FAQ JSON-LD — keyword-rich
    upsertJsonLd('faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: config.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a }
      }))
    });

    return () => {
      ['localbusiness', 'course', 'breadcrumb', 'faq'].forEach(removeJsonLd);
    };
  }, [slug, config]);

  if (!config) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-3">Page not found</h1>
          <Link to="/" className="text-red-600 underline">Go Home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-br from-red-700 via-red-600 to-orange-600 text-white py-14" data-testid="local-hero">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            <Badge className="bg-white/20 text-white border-0 mb-3">{config.examName} • Karol Bagh, Delhi</Badge>
            <h1 className="text-3xl md:text-5xl font-bold mb-4" data-testid="local-h1">{config.h1}</h1>
            <p className="text-lg md:text-xl text-red-100 mb-6">{config.subheading}</p>

            <div className="flex flex-wrap justify-center gap-4 text-sm md:text-base">
              <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" /> 4.6★ Rating (263+ Reviews)
              </span>
              <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Trophy className="w-4 h-4" /> 500+ Selections
              </span>
              <span className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Users className="w-4 h-4" /> 10,000+ Students Trained
              </span>
            </div>

            <div className="flex flex-wrap justify-center gap-3 mt-6">
              <Link to="/student/register">
                <Button data-testid="local-register-cta" className="bg-white text-red-700 hover:bg-red-50 px-6 py-2 font-semibold">
                  Enroll Now
                </Button>
              </Link>
              <a href={`tel:${PHONE.replace(/\s/g, '')}`}>
                <Button data-testid="local-call-cta" variant="outline" className="bg-transparent text-white border-white hover:bg-white/10 px-6 py-2">
                  <Phone className="w-4 h-4 mr-2" /> {PHONE}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Intro paragraph — keyword dense */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-4xl prose prose-lg">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-gray-900">
            {config.intro.heading}
          </h2>
          {config.intro.paragraphs.map((p, i) => (
            <p key={i} className="text-gray-700 leading-relaxed mb-4">{p}</p>
          ))}
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">{config.whyChoose.heading}</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {config.whyChoose.points.map((pt, i) => (
              <Card key={i} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-5">
                  <CheckCircle className="text-green-600 mb-2" size={24} />
                  <h3 className="font-bold text-base mb-2">{pt.title}</h3>
                  <p className="text-sm text-gray-600">{pt.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Course Features */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">What You Get in Our {config.examName} Coaching</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {config.features.map((f, i) => (
              <div key={i} className="flex items-start gap-3 bg-red-50 p-4 rounded-lg border border-red-100">
                <BookOpen className="text-red-600 flex-shrink-0 mt-1" size={20} />
                <div>
                  <h3 className="font-bold text-base">{f.title}</h3>
                  <p className="text-sm text-gray-700">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location & Contact */}
      <section className="py-12 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-6">Visit Our Karol Bagh Coaching Centre</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <div className="flex items-start gap-3 mb-4">
                <MapPin className="flex-shrink-0 mt-1" size={20} />
                <p>{ADDRESS}</p>
              </div>
              <div className="flex items-center gap-3 mb-3">
                <Phone size={20} /> <a href={`tel:${PHONE.replace(/\s/g, '')}`} className="hover:underline">{PHONE}</a>
              </div>
              <div className="flex items-center gap-3 mb-3">
                <Mail size={20} /> <a href="mailto:info@vidya365.com" className="hover:underline">info@vidya365.com</a>
              </div>
              <div className="flex items-center gap-3 mb-3">
                <Clock size={20} /> Mon–Sat: 9:00 AM – 8:00 PM
              </div>
            </div>
            <div>
              <h3 className="font-bold mb-3 text-lg">Coaching available for students from:</h3>
              <div className="flex flex-wrap gap-2">
                {NEARBY.map((area) => (
                  <Badge key={area} className="bg-white/20 text-white border-0">{area}</Badge>
                ))}
                <Badge className="bg-white/20 text-white border-0">All of Delhi NCR</Badge>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {config.faqs.map((f, i) => (
              <Card key={i} className="border-l-4 border-red-600">
                <CardContent className="p-5">
                  <h3 className="font-bold text-base mb-2">{f.q}</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{f.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Related Pages — internal linking for SEO */}
      <section className="py-10 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-xl md:text-2xl font-bold mb-5">Explore Other Coaching Programs in Karol Bagh, Delhi</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            {Object.entries(LOCAL_PAGES)
              .filter(([s]) => s !== slug)
              .slice(0, 8)
              .map(([s, c]) => (
                <Link key={s} to={`/${s}`} className="bg-white p-3 rounded-lg border hover:border-red-500 hover:text-red-600 transition-colors">
                  {c.shortName}
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 bg-red-600 text-white text-center">
        <div className="container mx-auto px-4 max-w-3xl">
          <GraduationCap className="mx-auto mb-3" size={48} />
          <h2 className="text-2xl md:text-3xl font-bold mb-3">{config.ctaHeadline}</h2>
          <p className="text-red-100 mb-6">{config.ctaSubtext}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/student/register">
              <Button data-testid="local-bottom-register" className="bg-white text-red-700 hover:bg-red-50 px-8 py-3 font-semibold">
                Enroll Now
              </Button>
            </Link>
            <Link to="/contact">
              <Button data-testid="local-bottom-contact" variant="outline" className="bg-transparent text-white border-white hover:bg-white/10 px-8 py-3">
                <Calendar className="w-4 h-4 mr-2" /> Book a Free Demo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LocalLandingPage;
